import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { API_URI } from "../../constants/GlobalConstants";
import {
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  Divider,
  alpha,
} from "@mui/material";
import {
  Search as SearchIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  FilterList as FilterIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useSnackbarNotification } from "../order/reusables/UseSnackBarNotification";
import { fetchSchedules } from "../../redux/actions/SchedulesActions";

const OrdersListScheduleTable = () => {
  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [orderStatus, setOrderStatus] = useState("");
  const [scheduled, setScheduled] = useState("");
  const [searchTag, setSearchTag] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [orderStatusUpdate, setOrderStatusUpdate] = useState("");
  const { showSnackbar, SnackbarComponent } = useSnackbarNotification();
  const dispatch = useDispatch();

  useEffect(() => {
    if (userInfo && userInfo.token) {
      const fetchOrders = async () => {
        try {
          setLoading(true);
          const response = await axios.get(`${API_URI}/api/schedules/orders`, {
            headers: {
              Authorization: `Bearer ${userInfo.token}`,
            },
            params: {
              page,
              limit: 30,
              orderStatus,
              scheduled,
              searchTag,
            },
          });
          setOrders(response.data.orders);
          setTotalPages(response.data.pages);
        } catch (err) {
          setError("Error fetching orders");
        } finally {
          setLoading(false);
        }
      };

      fetchOrders();
    }
  }, [userInfo, page, orderStatus, scheduled, searchTag]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSearch = () => {
    setSearchTag(searchQuery);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleScheduleClick = (order) => {
    if (order.scheduled === true) {
      showSnackbar("This order is already scheduled.", "error");
      return;
    }

    setSelectedOrder(order);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setScheduleDate(null);
  };

  const handleScheduleConfirm = () => {
    if (!selectedOrder || !scheduleDate) {
      setError("Please select a date for scheduling.");
      return;
    }

    axios
      .post(
        `${API_URI}/api/schedules/schedule`,
        {
          orderId: selectedOrder._id,
          scheduledDate: scheduleDate,
          statusToUpdate: orderStatusUpdate,
        },
        {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
      )
      .then(() => {
        setOpenDialog(false);
        setScheduleDate(null);
        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order._id === selectedOrder._id
              ? { ...order, scheduledDate: scheduleDate }
              : order
          )
        );
        showSnackbar("Order scheduled successfully!", "success");
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        dispatch(fetchSchedules(startOfMonth, endOfMonth, "month", null));
      })
      .catch(() => {
        setError("Error scheduling the order");
        showSnackbar("Error scheduling the order!", "error");
      });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "ready_to_process":
        return <HourglassIcon fontSize="small" />;
      case "processing":
        return <PlayArrowIcon fontSize="small" />;
      case "processed":
        return <CheckCircleIcon fontSize="small" />;
      case "delivered":
        return <ShippingIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ready_to_process":
        return "warning";
      case "processing":
        return "info";
      case "processed":
        return "success";
      case "delivered":
        return "primary";
      default:
        return "default";
    }
  };

  const getScheduledChip = (scheduled) => {
    if (scheduled) {
      return (
        <Chip
          label="Scheduled"
          color="success"
          size="small"
          icon={<CheckCircleIcon />}
          variant="outlined"
        />
      );
    }
    return (
      <Chip
        label="Not Scheduled"
        color="default"
        size="small"
        variant="outlined"
      />
    );
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" color="error.main" p={3}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Card 
      elevation={2}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: '#fff',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header Section */}
        <Box mb={3}>
          <Typography 
            variant="h5" 
            component="h2" 
            sx={{ 
              fontWeight: 600,
              color: '#1976d2',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <CalendarIcon />
            Orders Schedule
          </Typography>
          
          {/* Filters Section */}
          <Box sx={{ 
            backgroundColor: alpha('#f5f5f5', 0.5),
            borderRadius: 2,
            p: 2,
            mb: 2
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Order Status"
                  value={orderStatus}
                  onChange={(e) => setOrderStatus(e.target.value)}
                  size="small"
                  fullWidth
                  SelectProps={{ native: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                >
                  <option value="">All Status</option>
                  <option value="ready_to_process">Ready to Process</option>
                  <option value="processing">Processing</option>
                  <option value="processed">Processed</option>
                  <option value="delivered">Delivered</option>
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Schedule Status"
                  value={scheduled}
                  onChange={(e) => setScheduled(e.target.value)}
                  size="small"
                  fullWidth
                  SelectProps={{ native: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ScheduleIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                >
                  <option value="">All Orders</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="not_scheduled">Not Scheduled</option>
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Search by Tag"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton 
                          onClick={handleSearch}
                          size="small"
                          edge="end"
                        >
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Orders Table */}
        <TableContainer
          component={Paper}
          elevation={1}
          sx={{ 
            borderRadius: 2,
            overflow: 'auto',
            border: '1px solid #e0e0e0',
            maxHeight: '600px',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '4px',
              '&:hover': {
                background: '#a8a8a8',
              },
            },
          }}
        >
          <Table sx={{ minWidth: 650, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ 
                  fontWeight: 700,
                  color: '#1976d2',
                  fontSize: '0.875rem',
                  width: '140px',
                  minWidth: '140px'
                }}>
                  Order Date
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 700,
                  color: '#1976d2',
                  fontSize: '0.875rem',
                  width: '120px',
                  minWidth: '120px'
                }}>
                  Product Type
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 700,
                  color: '#1976d2',
                  fontSize: '0.875rem',
                  width: '130px',
                  minWidth: '130px'
                }}>
                  Status
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 700,
                  color: '#1976d2',
                  fontSize: '0.875rem',
                  width: '140px',
                  minWidth: '140px'
                }}>
                  Schedule Status
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 700,
                  color: '#1976d2',
                  fontSize: '0.875rem',
                  width: '120px',
                  minWidth: '120px',
                  position: 'sticky',
                  right: 100,
                  backgroundColor: '#f8f9fa',
                  zIndex: 1
                }}>
                  Action
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 700,
                  color: '#1976d2',
                  fontSize: '0.875rem',
                  width: '100px',
                  minWidth: '100px',
                  position: 'sticky',
                  right: 0,
                  backgroundColor: '#f8f9fa',
                  zIndex: 1
                }}>
                  Tag
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No orders found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order, index) => (
                  <TableRow 
                    key={order._id}
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: alpha('#1976d2', 0.04) 
                      },
                      backgroundColor: index % 2 === 0 ? '#fafafa' : '#fff'
                    }}
                  >
                    <TableCell sx={{ py: 2, width: '140px' }}>
                      <Typography variant="body2" fontWeight={500}>
                        {new Date(order.createdAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2, width: '120px' }}>
                      <Typography variant="body2" noWrap title={order.productType}>
                        {order.productType}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2, width: '130px' }}>
                      <Chip
                        label={order.status?.replace('_', ' ').toUpperCase() || 'N/A'}
                        color={getStatusColor(order.status)}
                        size="small"
                        icon={getStatusIcon(order.status)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, width: '140px' }}>
                      {getScheduledChip(order.scheduled)}
                    </TableCell>
                    <TableCell sx={{ 
                      py: 2, 
                      width: '120px',
                      position: 'sticky',
                      right: 100,
                      backgroundColor: index % 2 === 0 ? '#fafafa' : '#fff',
                      zIndex: 1,
                      '&:hover': {
                        backgroundColor: alpha('#1976d2', 0.04)
                      }
                    }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleScheduleClick(order)}
                        size="small"
                        startIcon={<ScheduleIcon />}
                        disabled={order.scheduled}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 500,
                          borderRadius: 1.5,
                          px: 2,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Schedule
                      </Button>
                    </TableCell>
                    <TableCell sx={{ 
                      py: 2, 
                      width: '100px',
                      position: 'sticky',
                      right: 0,
                      backgroundColor: index % 2 === 0 ? '#fafafa' : '#fff',
                      zIndex: 1,
                      '&:hover': {
                        backgroundColor: alpha('#1976d2', 0.04)
                      }
                    }}>
                      <Chip
                        label={order.tag || "No Tag"}
                        variant="outlined"
                        size="small"
                        color={order.tag ? "default" : "secondary"}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          mt={3}
          sx={{
            backgroundColor: alpha('#f5f5f5', 0.3),
            borderRadius: 1,
            p: 2
          }}
        >
          <Button
            variant="outlined"
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            startIcon={<span>←</span>}
            sx={{ textTransform: 'none' }}
          >
            Previous
          </Button>
          
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body2" color="text.secondary">
              Page
            </Typography>
            <Chip 
              label={`${page} of ${totalPages}`}
              variant="outlined"
              size="small"
              color="primary"
            />
          </Box>
          
          <Button
            variant="outlined"
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
            endIcon={<span>→</span>}
            sx={{ textTransform: 'none' }}
          >
            Next
          </Button>
        </Box>
      </CardContent>

      {/* Enhanced Dialog for Scheduling */}
      <Dialog 
        open={openDialog} 
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <ScheduleIcon color="primary" />
          Schedule Order
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box mb={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Select Date and Time"
                value={scheduleDate}
                onChange={setScheduleDate}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Box>
          
          <TextField
            select
            label="Update Order Status"
            value={orderStatusUpdate}
            onChange={(e) => setOrderStatusUpdate(e.target.value)}
            fullWidth
            size="small"
            SelectProps={{ native: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          >
            <option value="">Select Status</option>
            <option value="ready_to_process">Ready to Process</option>
            <option value="processing">Processing</option>
            <option value="processed">Processed</option>
            <option value="delivered">Delivered</option>
          </TextField>
          
          {error && (
            <Box mt={2} p={2} bgcolor="error.light" borderRadius={1}>
              <Typography variant="body2" color="error.main">
                {error}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button 
            onClick={handleDialogClose} 
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleScheduleConfirm} 
            variant="contained"
            color="primary"
            startIcon={<CheckCircleIcon />}
            sx={{ textTransform: 'none' }}
          >
            Confirm Schedule
          </Button>
        </DialogActions>
      </Dialog>
      
      <SnackbarComponent />
    </Card>
  );
};

export default OrdersListScheduleTable;